import { spacing } from '@gto/ui/theme/design-tokens'

/** How wide the dialog renders. `default` is the width every stored modal has. */
export type ModalWidth = 'small' | 'default' | 'large' | 'xlarge'

/**
 * Tailwind reads class names out of the source, so each one has to appear here
 * in full and cannot be built from the step. The pixel figure is not repeated
 * next to it: it is read back off the same token, so the admin preview always
 * scales its stage to the width the customer modal will really use.
 */
const MODAL_WIDTHS = {
  small: { className: 'max-w-announcement-modal-small', token: 'announcement-modal-small' },
  default: { className: 'max-w-announcement-modal', token: 'announcement-modal' },
  large: { className: 'max-w-announcement-modal-large', token: 'announcement-modal-large' },
  xlarge: { className: 'max-w-announcement-modal-xlarge', token: 'announcement-modal-xlarge' },
} as const satisfies Record<ModalWidth, { className: string; token: keyof typeof spacing }>

/** Every step an operator can pick, in the order the admin offers them. */
export const MODAL_WIDTH_CHOICES = Object.keys(MODAL_WIDTHS) as readonly ModalWidth[]

/**
 * Whether a width the API sent is a step this build renders.
 *
 * The union describes what an operator can author. A hand-edited row, or a step
 * a later backend adds, arrives as a string outside it, and the caller needs to
 * know that happened — the modal still renders, so nothing else reveals it.
 *
 * Scanning the choices rather than indexing the map is what makes this total:
 * `MODAL_WIDTHS` inherits `Object.prototype`, so indexing it with `constructor`
 * or `toString` returns a function instead of `undefined` and a `??` fallback
 * behind it never fires.
 */
export const isModalWidth = (value: string | undefined): value is ModalWidth =>
  value !== undefined && MODAL_WIDTH_CHOICES.includes(value as ModalWidth)

/**
 * Resolves any width, listed or not, to a step this build knows.
 *
 * A default parameter only answers `undefined`, never a string the union does
 * not list, and indexing the map with one of those yields no step — so reading
 * a field off it throws. The customer modal is mounted at the app root with no
 * error boundary, so that throw is a blank page rather than a mis-sized dialog.
 *
 * `default` is the width every published modal renders at today, so an
 * unrecognised value degrades to the current appearance.
 */
const stepFor = (width: string | undefined) =>
  isModalWidth(width) ? MODAL_WIDTHS[width] : MODAL_WIDTHS.default

export const modalWidthClass = (width?: ModalWidth): string => stepFor(width).className

/** The width the authored block layout is designed against, in pixels. */
export const modalDesignWidth = (width?: ModalWidth): number =>
  Number.parseInt(spacing[stepFor(width).token], 10)
